import { VariableSizeList } from 'react-window';
import { RefObject, useCallback, useLayoutEffect, useState } from 'react';

import { useLogViewerContext } from '../context';

export function useKeepAtBottom(
  outerRef: RefObject<HTMLDivElement>,
  listRef: RefObject<VariableSizeList>
) {
  const [isScrollAtBottom, setIsScrollAtBottom] = useState(false);
  const [keepAtBottom, setKeepAtBottom] = useState(false);

  const { logs, setVisibleStartIndex } = useLogViewerContext();

  const checkIsScrollAtBottom = useCallback(() => {
    const ref = outerRef.current;
    if (ref) {
      const isBottom = ref.scrollHeight - ref.scrollTop - ref.clientHeight < 10;
      setIsScrollAtBottom(isBottom);
      return isBottom;
    }
    return false;
  }, [outerRef]);

  const onItemsRendered = useCallback(
    ({ visibleStartIndex }) => {
      setVisibleStartIndex(visibleStartIndex);
      checkIsScrollAtBottom();
    },
    [checkIsScrollAtBottom, setVisibleStartIndex]
  );

  const onScroll = useCallback(
    ({ scrollUpdateWasRequested }) => {
      const isAtBottom = checkIsScrollAtBottom();
      if (!scrollUpdateWasRequested) {
        setKeepAtBottom(isAtBottom);
      }
    },
    [checkIsScrollAtBottom]
  );

  const logLength = logs.logs.length;

  const scrollToBottom = useCallback(() => {
    setKeepAtBottom(true);
    if (logLength > 0) {
      const lastIndex = logLength - 1;
      // to handle large logs, scroll to the end of the last log, not the start of the last log
      listRef.current?.scrollToItem(lastIndex, 'end');
    }
  }, [logLength, listRef]);

  useLayoutEffect(() => {
    if (keepAtBottom) {
      scrollToBottom();
    }
  }, [keepAtBottom, logLength, scrollToBottom]);

  return { isScrollAtBottom, onItemsRendered, onScroll, scrollToBottom };
}
